import { useState, FormEvent } from 'react';
import FadeInSection from '@/components/feature/FadeInSection';

const PROJECT_ID = '8a4f04ed-3960-472b-978d-5363f01a3123';
const SEND_CONTACT_EMAIL_URL = 'https://debuqhzswiqcosjkuktw.supabase.co/functions/v1/send-contact-email';
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactFormSection() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    const name = formData.get('name') as string;
    const age = formData.get('age') as string;
    const gender = formData.get('gender') as string;
    const phone = formData.get('phone') as string;
    const email = formData.get('email') as string;
    const service = formData.get('service') as string;

    try {
      const response = await fetch(SEND_CONTACT_EMAIL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          project_id: PROJECT_ID,
          email: email,
          body: {
            'お名前': name,
            '年齢': `${age}歳`,
            '性別': gender,
            '電話番号': phone,
            'メールアドレス': email,
            'ご希望のサービス': service,
            'お問い合わせ内容': message || '',
          },
        }),
      });

      const responseText = await response.text();
      let parsed: { success?: boolean; error?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // non-JSON response
      }

      if (!response.ok || parsed.success !== true) {
        const serverError = parsed.error || `サーバーエラー (${response.status})`;
        throw new Error(serverError);
      }

      setStatus('success');
      form.reset();
      setCharCount(0);
    } catch (err) {
      console.error('送信エラー:', err);
      const msg = err instanceof Error ? err.message : '送信中にエラーが発生しました。';
      setErrorMessage(msg || '送信中にエラーが発生しました。しばらく経ってから再度お試しいただくか、お電話にてご連絡ください。');
      setStatus('error');
    }
  };

  const labelStyle = {
    display: 'block',
    color: '#4a3728',
    fontSize: '17px',
    fontWeight: '600' as const,
    marginBottom: '8px',
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 18px',
    borderRadius: '8px',
    border: '2px solid #e0cfc4',
    fontSize: '17px',
    color: '#4a3728',
    background: '#fff',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  if (status === 'success') {
    return (
      <section id="contact-form" className="section-padding" style={{ background: '#f2f8f4' }}>
        <div className="content-width">
          <div
            className="max-w-2xl mx-auto rounded-2xl p-14 text-center"
            style={{ background: '#fff', border: '2px solid #5a9e6f' }}
          >
            <div
              className="mx-auto mb-6 flex items-center justify-center w-20 h-20 rounded-full"
              style={{ background: '#f2f8f4' }}
            >
              <span className="flex items-center justify-center w-12 h-12">
                <i className="ri-checkbox-circle-fill text-[#5a9e6f] text-5xl"></i>
              </span>
            </div>
            <h2 style={{ color: '#5a9e6f', marginBottom: '16px' }}>
              お問い合わせありがとうございます
            </h2>
            <p style={{ color: '#5a4a3e', fontSize: '18px', lineHeight: '2.0' }}>
              お送りいただいた内容を確認のうえ、<br />
              営業日中（10:00〜18:00）にご連絡いたします。<br />
              しばらくお待ちくださいませ。
            </p>
            <button
              onClick={() => setStatus('idle')}
              className="mt-8 px-8 py-4 rounded-lg font-bold whitespace-nowrap transition-colors duration-200"
              style={{ background: '#e8934e', color: '#fff', fontSize: '17px' }}
            >
              別のお問い合わせをする
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-form" className="section-padding" style={{ background: '#f2f8f4' }}>
      <div className="content-width">
        <FadeInSection className="text-center mb-14">
          <p
            className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-4"
            style={{ background: 'rgba(90,158,111,0.12)', color: '#5a9e6f' }}
          >
            お問い合わせフォーム
          </p>
          <h2 style={{ color: '#8b4513' }}>フォームからのお問い合わせ</h2>
          <p className="mt-4 mx-auto max-w-xl" style={{ color: '#6b5a4e', fontSize: '17px', lineHeight: '1.8' }}>
            下記フォームにご記入のうえ、送信ボタンを押してください。<br className="hidden sm:block" />
            営業日中にご連絡いたします。
          </p>
        </FadeInSection>

        <FadeInSection>
          <form
            id="wahaha-contact-form"
            onSubmit={handleSubmit}
            className="max-w-2xl mx-auto rounded-2xl p-8 md:p-12"
            style={{ background: '#fff', border: '2px solid rgba(90,158,111,0.2)' }}
          >
            {/* お名前 */}
            <div className="mb-8">
              <label htmlFor="name" style={labelStyle}>
                お名前
                <span className="ml-2 px-2 py-0.5 rounded text-xs font-bold" style={{ background: '#e8934e', color: '#fff' }}>必須</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                placeholder="例：山田 花子"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = '#5a9e6f'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0cfc4'; }}
              />
            </div>

            {/* 年齢 */}
            <div className="mb-8">
              <label htmlFor="age" style={labelStyle}>
                年齢
                <span className="ml-2 px-2 py-0.5 rounded text-xs font-bold" style={{ background: '#e8934e', color: '#fff' }}>必須</span>
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  id="age"
                  name="age"
                  required
                  min={20}
                  max={99}
                  placeholder="例：65"
                  style={{ ...inputStyle, maxWidth: '160px' }}
                  onFocus={(e) => { e.target.style.borderColor = '#5a9e6f'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e0cfc4'; }}
                />
                <span style={{ color: '#4a3728', fontSize: '17px' }}>歳</span>
              </div>
            </div>

            {/* 性別 */}
            <div className="mb-8">
              <p style={{ ...labelStyle, marginBottom: '14px' }}>
                性別
                <span className="ml-2 px-2 py-0.5 rounded text-xs font-bold" style={{ background: '#e8934e', color: '#fff' }}>必須</span>
              </p>
              <div className="flex gap-6 flex-wrap">
                {[
                  { value: '男性', label: '男性' },
                  { value: '女性', label: '女性' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-3 cursor-pointer"
                    style={{ fontSize: '17px', color: '#4a3728' }}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value={opt.value}
                      required
                      style={{ width: '22px', height: '22px', accentColor: '#5a9e6f' }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {/* 電話番号 */}
            <div className="mb-8">
              <label htmlFor="phone" style={labelStyle}>
                電話番号
                <span className="ml-2 px-2 py-0.5 rounded text-xs font-bold" style={{ background: '#e8934e', color: '#fff' }}>必須</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                placeholder="例：090-1234-5678"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = '#5a9e6f'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0cfc4'; }}
              />
            </div>

            {/* メールアドレス */}
            <div className="mb-8">
              <label htmlFor="email" style={labelStyle}>
                メールアドレス
                <span className="ml-2 px-2 py-0.5 rounded text-xs font-bold" style={{ background: '#e8934e', color: '#fff' }}>必須</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                placeholder="例：hanako@example.com"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = '#5a9e6f'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0cfc4'; }}
              />
            </div>

            {/* ご希望のサービス */}
            <div className="mb-8">
              <label htmlFor="service" style={labelStyle}>
                ご希望のサービス
                <span className="ml-2 px-2 py-0.5 rounded text-xs font-bold" style={{ background: '#e8934e', color: '#fff' }}>必須</span>
              </label>
              <div className="relative">
                <select
                  id="service"
                  name="service"
                  required
                  defaultValue=""
                  style={{
                    ...inputStyle,
                    appearance: 'none',
                    paddingRight: '48px',
                    cursor: 'pointer',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#5a9e6f'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e0cfc4'; }}
                >
                  <option value="" disabled>選択してください</option>
                  <option value="無料相談を希望したい">無料相談を希望したい</option>
                  <option value="話だけ聞いてみたい">話だけ聞いてみたい</option>
                  <option value="サービスについて知りたい">サービスについて知りたい</option>
                  <option value="その他">その他</option>
                </select>
                <span
                  className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center w-5 h-5"
                >
                  <i className="ri-arrow-down-s-line text-[#8b7b6e] text-xl"></i>
                </span>
              </div>
            </div>

            {/* お問い合わせ内容 */}
            <div className="mb-10">
              <label htmlFor="message" style={labelStyle}>
                お問い合わせ内容
                <span className="ml-2 px-2 py-0.5 rounded text-xs" style={{ background: '#e8d8cc', color: '#8b4513', fontWeight: '600' }}>任意</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                maxLength={500}
                placeholder="ご質問・ご要望などをご自由にお書きください（500文字以内）"
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  minHeight: '140px',
                }}
                onChange={(e) => setCharCount(e.target.value.length)}
                onFocus={(e) => { e.target.style.borderColor = '#5a9e6f'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0cfc4'; }}
              />
              <p
                className="mt-2 text-right"
                style={{ color: charCount > 500 ? '#c0392b' : '#8b7b6e', fontSize: '14px' }}
              >
                {charCount} / 500文字
              </p>
            </div>

            {/* Error message */}
            {status === 'error' && (
              <div
                className="mb-6 p-4 rounded-lg"
                style={{ background: '#fff1f0', border: '1px solid #ffb3b3', color: '#c0392b', fontSize: '16px' }}
              >
                <i className="ri-error-warning-line mr-2"></i>
                {errorMessage}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={status === 'submitting' || charCount > 500}
              className="w-full py-5 rounded-lg font-bold whitespace-nowrap transition-all duration-300 flex items-center justify-center gap-3"
              style={{
                background: status === 'submitting' ? '#c8a882' : '#e8934e',
                color: '#fff',
                fontSize: '19px',
                cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
              }}
            >
              {status === 'submitting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  送信中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill"></i>
                  送信する
                </>
              )}
            </button>

            <p className="mt-6 text-center" style={{ color: '#8b7b6e', fontSize: '14px', lineHeight: '1.8' }}>
              送信いただいた個人情報は、お問い合わせへの返答のみに使用し、<br className="hidden sm:block" />
              第三者への提供は一切行いません。
            </p>
          </form>
        </FadeInSection>
      </div>
    </section>
  );
}